import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Heart, Upload as UploadIcon, Globe, Plus, Sparkles } from 'lucide-react';
import { useModelsStore } from '@/stores/models-store';
import { useAuthStore } from '@/stores/auth-store';
import { useUIStore } from '@/stores/ui-store';
import ModelCard from '@/components/features/models/ModelCard';
import UserModelCard from '@/components/features/models/UserModelCard';
import UserModelDetail from '@/pages/UserModelDetail';
import UploadModelDrawer from '@/pages/UploadModelDrawer';
import { ModelCardSkeleton } from '@/components/ui/Skeleton';
import Button from '@/components/ui/Button';
import type { UserModel } from '@visionar/shared';

type LibraryTab = 'favorites' | 'my-models' | 'public';

const tabs: { id: LibraryTab; icon: React.ElementType; label: string; shortLabel: string }[] = [
  { id: 'favorites', icon: Heart, label: 'Favorites', shortLabel: 'Favs' },
  { id: 'my-models', icon: UploadIcon, label: 'My Models', shortLabel: 'Mine' },
  { id: 'public', icon: Globe, label: 'Public', shortLabel: 'Public' },
];

export default function Library() {
  const {
    favorites,
    userModels,
    publicModels,
    isLoadingFavorites,
    isLoadingUserModels,
    isLoadingPublicModels,
    fetchFavorites,
    fetchUserModels,
    fetchPublicModels,
  } = useModelsStore();

  const { isAuthenticated } = useAuthStore();
  const {
    setAuthView,
    selectedUserModel,
    setSelectedUserModel,
    isUploadDrawerOpen,
    setUploadDrawerOpen,
  } = useUIStore();


  const [activeTab, setActiveTab] = useState<LibraryTab>('favorites');

  useEffect(() => {
    if (isAuthenticated) {
      fetchFavorites();
      fetchUserModels();
    }
  }, [isAuthenticated, fetchFavorites, fetchUserModels]);

  useEffect(() => {
    if (activeTab === 'public') {
      fetchPublicModels();
    }
  }, [activeTab, fetchPublicModels]);

  // Track previous upload drawer state to know when it closes
  const [wasUploadOpen, setWasUploadOpen] = useState(false);

  useEffect(() => {
    // When drawer transitions from open to closed, refresh and switch tab
    if (wasUploadOpen && !isUploadDrawerOpen && isAuthenticated) {
      fetchUserModels();
      setActiveTab('my-models');
    }
    setWasUploadOpen(isUploadDrawerOpen);
  }, [isUploadDrawerOpen, isAuthenticated, fetchUserModels, wasUploadOpen]);

  const handleUserModelClick = (model: UserModel) => {
    setSelectedUserModel(model);
  };

  if (!isAuthenticated) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="h-full flex flex-col items-center justify-center px-6 text-center relative"
      >
        {/* Background gradient */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[400px] h-[400px] rounded-full bg-pink-500/10 blur-[100px]" />
        </div>

        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="relative w-24 h-24 rounded-full glass flex items-center justify-center mb-6"
        >
          <Heart size={40} className="text-white/40" />
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-2xl font-bold mb-2"
        >
          Your Library
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-white/50 mb-8 max-w-xs"
        >
          Sign in to save your favorite models and upload your own 3D creations.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Button onClick={() => setAuthView('login')}>Sign In to Continue</Button>
        </motion.div>
      </motion.div>
    );
  }

  const getTabCount = (tab: LibraryTab) => {
    switch (tab) {
      case 'favorites':
        return favorites.length;
      case 'my-models':
        return userModels.length;
      case 'public':
        return publicModels.length;
    }
  };

  return (
    <>
      {/* User Model Detail Drawer */}
      <AnimatePresence>
        {selectedUserModel && <UserModelDetail model={selectedUserModel} />}
      </AnimatePresence>

      {/* Upload Drawer */}
      <AnimatePresence>
        {isUploadDrawerOpen && <UploadModelDrawer />}
      </AnimatePresence>

      {/* Main Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="h-full overflow-y-auto no-scrollbar pb-32"
      >
        {/* Background gradient */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -left-40 w-[300px] h-[300px] rounded-full bg-pink-500/8 blur-[80px]" />
          <div className="absolute top-1/3 -right-20 w-[250px] h-[250px] rounded-full bg-accent-blue/8 blur-[60px]" />
        </div>

        {/* Safe Area Spacer */}
        <div className="safe-area-top" />

        {/* Header */}
        <div className="relative px-6 pt-4 pb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Library</h1>
            <p className="text-white/50 text-sm mt-0.5">Your collection and uploads</p>
          </div>
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => setUploadDrawerOpen(true)}
            className="w-12 h-12 rounded-full bg-gradient-to-br from-accent-blue to-accent-purple flex items-center justify-center shadow-lg shadow-accent-blue/30"
          >
            <Plus size={24} />
          </motion.button>
        </div>

        {/* Stats */}
        <div className="relative px-6 mb-6">
          <div className="grid grid-cols-3 gap-2.5">
            {[
              { icon: Heart, count: favorites.length, label: 'Favorites', color: 'text-pink-400', bg: 'bg-pink-500/15' },
              { icon: UploadIcon, count: userModels.length, label: 'Uploaded', color: 'text-accent-blue', bg: 'bg-accent-blue/15' },
              { icon: Globe, count: publicModels.length, label: 'Public', color: 'text-accent-purple', bg: 'bg-accent-purple/15' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.05 }}
                className="glass rounded-2xl p-3.5"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <div className={`w-7 h-7 rounded-full ${stat.bg} flex items-center justify-center flex-shrink-0`}>
                    <stat.icon size={13} className={stat.color} />
                  </div>
                  <span className="text-xl font-bold">{stat.count}</span>
                </div>
                <p className="text-white/40 text-[11px] font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Tabs - Compact style like bottom nav */}
        <div className="relative px-6 mb-5">
          <div className="glass rounded-2xl p-1.5 flex">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              const Icon = tab.icon;
              const count = getTabCount(tab.id);
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative flex-1 py-2.5 rounded-xl transition-colors flex flex-col items-center justify-center gap-0.5 ${
                    isActive ? 'text-white' : 'text-white/40'
                  }`}
                >
                  {isActive && (
                    <motion.div
                      layoutId="library-tab"
                      className="absolute inset-0.5 bg-white/10 rounded-xl"
                      transition={{ type: 'spring', bounce: 0.15, duration: 0.5 }}
                    />
                  )}
                  {/* Icon with badge */}
                  <div className="relative z-10">
                    <Icon size={20} strokeWidth={isActive ? 2.5 : 2} />
                    {count > 0 && (
                      <span className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 text-[10px] font-bold rounded-full bg-accent-blue text-white flex items-center justify-center">
                        {count > 99 ? '99+' : count}
                      </span>
                    )}
                  </div>
                  {/* Short label */}
                  <span className="relative z-10 text-[10px] font-semibold mt-0.5">
                    {tab.shortLabel}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="relative px-6">
          <AnimatePresence mode="wait">
            {/* Favorites Tab */}
            {activeTab === 'favorites' && (
              <motion.div
                key="favorites"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.2 }}
              >
                {isLoadingFavorites ? (
                  <div className="grid grid-cols-2 gap-3">
                    <ModelCardSkeleton />
                    <ModelCardSkeleton />
                  </div>
                ) : favorites.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="glass rounded-3xl p-8 text-center"
                  >
                    <div className="w-16 h-16 rounded-full bg-pink-500/10 flex items-center justify-center mx-auto mb-4">
                      <Heart size={28} className="text-pink-400/50" />
                    </div>
                    <h3 className="font-bold mb-2">No favorites yet</h3>
                    <p className="text-white/50 text-sm">
                      Tap the heart icon on any model to add it to your favorites.
                    </p>
                  </motion.div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {favorites.map((fav, index) =>
                      fav.model ? (
                        <motion.div
                          key={fav.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <ModelCard model={fav.model} size="sm" />
                        </motion.div>
                      ) : null
                    )}
                  </div>
                )}
              </motion.div>
            )}

            {/* My Models Tab */}
            {activeTab === 'my-models' && (
              <motion.div
                key="my-models"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.2 }}
              >
                {isLoadingUserModels ? (
                  <div className="grid grid-cols-2 gap-3">
                    <ModelCardSkeleton />
                    <ModelCardSkeleton />
                  </div>
                ) : userModels.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="glass rounded-3xl p-8 text-center"
                  >
                    <div className="w-16 h-16 rounded-full bg-accent-blue/10 flex items-center justify-center mx-auto mb-4">
                      <Sparkles size={28} className="text-accent-blue/50" />
                    </div>
                    <h3 className="font-bold mb-2">No models uploaded</h3>
                    <p className="text-white/50 text-sm mb-5">
                      Upload your first 3D model to see it here.
                    </p>
                    <div className="flex justify-center">
                      <Button
                        onClick={() => setUploadDrawerOpen(true)}
                        icon={<UploadIcon size={18} />}
                      >
                        Upload Model
                      </Button>
                    </div>
                  </motion.div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {userModels.map((model, index) => (
                      <motion.div
                        key={model.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <UserModelCard
                          model={model}
                          onClick={handleUserModelClick}
                        />
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Public Models Tab */}
            {activeTab === 'public' && (
              <motion.div
                key="public"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.2 }}
              >
                {isLoadingPublicModels ? (
                  <div className="grid grid-cols-2 gap-3">
                    <ModelCardSkeleton />
                    <ModelCardSkeleton />
                  </div>
                ) : publicModels.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="glass rounded-3xl p-8 text-center"
                  >
                    <div className="w-16 h-16 rounded-full bg-accent-purple/10 flex items-center justify-center mx-auto mb-4">
                      <Globe size={28} className="text-accent-purple/50" />
                    </div>
                    <h3 className="font-bold mb-2">No public models yet</h3>
                    <p className="text-white/50 text-sm">
                      Be the first to share a model with the community!
                    </p>
                  </motion.div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {publicModels.map((model, index) => (
                      <motion.div
                        key={model.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <UserModelCard
                          model={model}
                          onClick={handleUserModelClick}
                        />
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </>
  );
}
